import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

const INTENT_STATEMENT =
  'I agree that typing my name above constitutes my electronic ' +
  'signature under the U.S. E-SIGN Act and applicable state law.';
const CONSENT_STATEMENT =
  'I consent to receive and sign this agreement electronically.';

const normalize = (s) => (s || '').trim().toLowerCase();

const PublicLeaseSign = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [typedName, setTypedName] = useState('');
  const [intentChecked, setIntentChecked] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/public/leases/sign/${token}`)
      .then((res) => {
        if (!cancelled) setInfo(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.detail ||
            'This signing link has expired or been used.'
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const nameMatches = useMemo(() => {
    if (!info?.customer_name) return false;
    return normalize(typedName) === normalize(info.customer_name);
  }, [typedName, info]);

  const canSubmit =
    nameMatches && intentChecked && consentChecked && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(
        `${API_URL}/api/public/leases/sign/${token}/submit`,
        {
          signature_name: typedName.trim(),
          intent_acknowledged: intentChecked,
          consent_acknowledged: consentChecked,
        }
      );
      navigate(`/lease/sign/${token}/done`);
    } catch (err) {
      setSubmitError(
        err?.response?.data?.detail ||
          'Could not submit your signature. Please try again.'
      );
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div
          role="status"
          aria-label="Loading"
          className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"
        />
      </div>
    );
  }

  if (loadError || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC] px-4">
        <div className="max-w-md text-center">
          <h1 className="text-[15px] font-semibold text-slate-900 mb-2">
            Signing link unavailable
          </h1>
          <p className="text-[13px] text-slate-500">
            {loadError ||
              'This signing link has expired or been used.'}
            {info?.facility_name ? (
              <>
                {' '}
                Contact {info.facility_name}
                {info.facility_phone ? ` at ${info.facility_phone}` : ''} for a
                new link.
              </>
            ) : (
              <> Please contact the facility for a new link.</>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      {/* Header band */}
      <header className="border-b border-slate-200/80 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <p className="text-[12px] font-medium uppercase tracking-wide text-slate-500">
            {info.facility_name}
          </p>
          <h1 className="text-[20px] font-semibold text-slate-900 mt-1">
            Sign your lease
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Review the agreement below, then sign at the bottom.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* PDF viewer */}
        <section className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
          <iframe
            title="Lease Agreement"
            src={`${API_URL}/api/public/leases/sign/${token}/pdf`}
            className="w-full"
            style={{ height: '800px', border: 'none' }}
          />
        </section>

        {/* Signature panel */}
        <section className="rounded-xl border border-slate-200/80 bg-white p-6">
          <h2 className="text-[15px] font-semibold text-slate-900 mb-4">
            Electronic Signature
          </h2>

          {info.unit_number && (
            <div className="grid grid-cols-2 gap-3 mb-4 text-[12px]">
              <div>
                <span className="text-slate-500">Unit</span>
                <p className="text-slate-900 font-medium">{info.unit_number}</p>
              </div>
              {info.monthly_rate != null && (
                <div>
                  <span className="text-slate-500">Monthly rate</span>
                  <p className="text-slate-900 font-medium">
                    ${Number(info.monthly_rate).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          )}

          <p className="text-[13px] text-slate-600 mb-4">
            Signing as:{' '}
            <span className="font-medium text-slate-900">
              {info.customer_name}
            </span>
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="signature_name"
                className="block text-[12px] font-medium text-slate-500 mb-1"
              >
                Type your full name to sign
              </label>
              <input
                id="signature_name"
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder={info.customer_name}
                autoComplete="off"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {typedName.trim().length > 0 && !nameMatches && (
                <p className="mt-1 text-[12px] text-amber-600">
                  Typed name must match the customer name on file.
                </p>
              )}
            </div>

            <label className="flex items-start gap-3 text-[13px] text-slate-700">
              <input
                type="checkbox"
                checked={intentChecked}
                onChange={(e) => setIntentChecked(e.target.checked)}
                className="mt-1"
              />
              <span>{INTENT_STATEMENT}</span>
            </label>

            <label className="flex items-start gap-3 text-[13px] text-slate-700">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-1"
              />
              <span>{CONSENT_STATEMENT}</span>
            </label>

            {submitError && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
              >
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full rounded-lg bg-blue-600 text-white text-[13px] font-medium py-2.5 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Signing…' : 'Sign Lease'}
            </button>

            <p className="text-[11px] text-slate-500">
              Your IP address and signature timestamp will be recorded as part
              of the legally binding signature.
            </p>
          </form>
        </section>
      </main>
    </div>
  );
};

export default PublicLeaseSign;
